import { clsx } from "clsx";
import {
  AlertCircle,
  Calendar,
  Check,
  CheckCircle,
  ChevronDown,
  ChevronRight,
  Clock,
  FileUp,
  History,
  ListChecks,
  Loader2,
  Pencil,
  Sparkles,
  Trash2,
  Upload,
  XCircle,
} from "lucide-react";
import { type ChangeEvent, useEffect, useRef, useState, type DragEvent } from "react";
import { Badge, Button, Card, Modal, Select, Tabs } from "@/components/common";
import { UsersPagination } from "@/components/admin/UsersPagination";
import { AgentSelect } from "@/components/imports/AgentSelect";
import { MonthChecklistSection } from "@/components/imports/MonthChecklistSection";
import { PageHeader } from "@/components/layout/PageHeader";
import { useUser } from "@/hooks/useAuth";
import {
  useBatchPreviewImport,
  useCancelImport,
  useConfirmImport,
  useDeleteImport,
  useDetectInsurer,
  useImportHistory,
  useInsurers,
  usePreviewImport,
} from "@/hooks/useSettlements";
import type {
  BatchImportPreviewItem,
  DetectionResult,
  ImportHistoryItem,
  ImportPreview,
  ImportResult,
  OfwcaMapping,
  OfwcaPreviewItem,
  OrphanedAssignment,
  OrphanedPolicyPreview,
  PolicyOverride,
} from "@/types/settlement";

type ImportStep = "upload" | "preview" | "result";

const MONTHS = [
  { value: "1", label: "Styczeń" },
  { value: "2", label: "Luty" },
  { value: "3", label: "Marzec" },
  { value: "4", label: "Kwiecień" },
  { value: "5", label: "Maj" },
  { value: "6", label: "Czerwiec" },
  { value: "7", label: "Lipiec" },
  { value: "8", label: "Sierpień" },
  { value: "9", label: "Wrzesień" },
  { value: "10", label: "Październik" },
  { value: "11", label: "Listopad" },
  { value: "12", label: "Grudzień" },
];

function yearOptions(): { value: string; label: string }[] {
  const currentYear = new Date().getFullYear();
  const options = [];
  for (let y = currentYear; y >= currentYear - 3; y--) {
    options.push({ value: String(y), label: String(y) });
  }
  return options;
}

function formatCurrency(value: number): string {
  return new Intl.NumberFormat("pl-PL", {
    style: "currency",
    currency: "PLN",
  }).format(value);
}

const STEP_LABELS: Record<ImportStep, string> = {
  upload: "Przesyłanie pliku",
  preview: "Podgląd i mapowanie",
  result: "Wynik importu",
};

const STEPS: ImportStep[] = ["upload", "preview", "result"];

const IMPORT_TABS = [
  { id: "import", label: "Import", icon: <Upload size={16} /> },
  { id: "history", label: "Historia", icon: <History size={16} /> },
];

// Only administrators may see which insurers paid commission in a month.
const ADMIN_TABS = [
  ...IMPORT_TABS,
  { id: "checklist", label: "Kontrola miesiąca", icon: <ListChecks size={16} /> },
];

const STATUS_MAP: Record<
  string,
  {
    label: string;
    variant: "success" | "warning" | "error" | "info" | "default";
  }
> = {
  Completed: { label: "Zakończony", variant: "success" },
  CompletedWithErrors: { label: "Z błędami", variant: "warning" },
  Failed: { label: "Nieudany", variant: "error" },
  AwaitingMapping: { label: "Oczekuje na mapowanie", variant: "info" },
  Processing: { label: "Przetwarzanie", variant: "info" },
  Pending: { label: "Oczekujący", variant: "default" },
};

export function ImportPage() {
  const [activeTab, setActiveTab] = useState("import");
  const user = useUser();
  const isAdmin = user?.role === "Administrator";

  return (
    <div className="animate-fade-in space-y-4">
      <PageHeader
        title="Import danych"
        description="Importuj dane prowizji z pliku Excel lub CSV"
      />

      <Tabs
        tabs={isAdmin ? ADMIN_TABS : IMPORT_TABS}
        activeTab={activeTab}
        onChange={setActiveTab}
      />

      {activeTab === "import" && <ImportFlow />}
      {activeTab === "history" && <ImportHistorySection />}
      {activeTab === "checklist" && isAdmin && <MonthChecklistSection />}
    </div>
  );
}

function ImportFlow() {
  const [step, setStep] = useState<ImportStep>("upload");
  const [file, setFile] = useState<File | null>(null);
  const [insurerId, setInsurerId] = useState("");
  const [year, setYear] = useState(String(new Date().getFullYear()));
  const [month, setMonth] = useState(String(new Date().getMonth() + 1));
  const [preview, setPreview] = useState<ImportPreview | null>(null);
  const [result, setResult] = useState<ImportResult | null>(null);
  const [mappings, setMappings] = useState<Record<string, string>>({});
  const [orphanedAssignments, setOrphanedAssignments] = useState<Record<number, string>>({}); // rowNumber -> agentId
  const [detection, setDetection] = useState<DetectionResult | null>(null);
  const [autoDetected, setAutoDetected] = useState(false);
  const [autoDetectedDate, setAutoDetectedDate] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [dropError, setDropError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const currentFileRef = useRef<File | null>(null);
  const dragCounter = useRef(0);

  const ALLOWED_EXTENSIONS = [".xlsx", ".xls", ".csv", ".pdf"];
  const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10 MB

  useEffect(() => {
    if (!dropError) return;
    const id = setTimeout(() => setDropError(null), 4000);
    return () => clearTimeout(id);
  }, [dropError]);

  function validateFile(file: File): string | null {
    const ext = "." + file.name.split(".").pop()?.toLowerCase();
    if (!ALLOWED_EXTENSIONS.includes(ext))
      return "Nieobsługiwany format pliku. Dozwolone: .xlsx, .xls, .csv, .pdf";
    if (file.size > MAX_FILE_SIZE)
      return "Plik przekracza maksymalny rozmiar 10 MB.";
    return null;
  }

  // Per-policy override state
  const [policyOverrides, setPolicyOverrides] = useState<Record<string, string>>({});
  const [expandedOfwca, setExpandedOfwca] = useState<Set<string>>(new Set());

  // Multi-insurer batch state
  const [selectedInsurerIds, setSelectedInsurerIds] = useState<string[]>([]);
  const [batchPreviews, setBatchPreviews] = useState<BatchImportPreviewItem[]>([]);
  const [currentBatchIndex, setCurrentBatchIndex] = useState(0);
  const [batchResults, setBatchResults] = useState<(ImportResult | "skipped" | "error")[]>([]);

  const { data: insurers, isLoading: insurersLoading } = useInsurers();
  const detectMutation = useDetectInsurer();
  const previewMutation = usePreviewImport();
  const confirmMutation = useConfirmImport();
  const batchPreviewMutation = useBatchPreviewImport();
  const cancelImportMutation = useCancelImport();

  const highConfidenceCandidates = detection?.candidates.filter(c => c.score >= 0.95) ?? [];
  const isMultiInsurer = highConfidenceCandidates.length >= 2;

  useEffect(() => {
    if (detection) {
      const high = detection.candidates.filter(c => c.score >= 0.95);
      if (high.length >= 2) {
        setSelectedInsurerIds(high.map(c => c.insurerId));
      }
    }
  }, [detection]);

  function handleFileChange(e: ChangeEvent<HTMLInputElement>) {
    const selected = e.target.files?.[0] ?? null;
    if (fileInputRef.current) fileInputRef.current.value = "";
    if (!selected) return;

    const error = validateFile(selected);
    if (error) {
      setDropError(error);
      return;
    }

    currentFileRef.current = selected;
    setFile(selected);
    setDetection(null);
    setAutoDetected(false);
    setAutoDetectedDate(false);
    previewMutation.reset();
    detectMutation.reset();

    if (selected) {
      const capturedFile = selected;
      detectMutation.mutate(selected, {
        onSuccess: (data) => {
          if (currentFileRef.current !== capturedFile) return;
          setDetection(data);
          if (data.isExactMatch && data.detectedInsurer) {
            setInsurerId(data.detectedInsurer.insurerId);
            setAutoDetected(true);
          }
          if (data.detectedYear && data.detectedMonth) {
            setYear(String(data.detectedYear));
            setMonth(String(data.detectedMonth));
            setAutoDetectedDate(true);
          }
        },
      });
    }
  }

  function handleDragOver(e: DragEvent<HTMLDivElement>) {
    e.preventDefault();
    e.stopPropagation();
  }

  function handleDragEnter(e: DragEvent<HTMLDivElement>) {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current++;
    if (dragCounter.current === 1) {
      setIsDragging(true);
    }
  }

  function handleDragLeave(e: DragEvent<HTMLDivElement>) {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current = Math.max(0, dragCounter.current - 1);
    if (dragCounter.current === 0) {
      setIsDragging(false);
    }
  }

  function handleDrop(e: DragEvent<HTMLDivElement>) {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current = 0;
    setIsDragging(false);

    const droppedFile = e.dataTransfer.files[0];
    if (!droppedFile) return;

    const error = validateFile(droppedFile);
    if (error) {
      setDropError(error);
      return;
    }

    currentFileRef.current = droppedFile;
    setFile(droppedFile);
    setDetection(null);
    setAutoDetected(false);
    setAutoDetectedDate(false);
    previewMutation.reset();
    detectMutation.reset();

    const capturedFile = droppedFile;
    detectMutation.mutate(droppedFile, {
      onSuccess: (data) => {
        if (currentFileRef.current !== capturedFile) return;
        setDetection(data);
        if (data.isExactMatch && data.detectedInsurer) {
          setInsurerId(data.detectedInsurer.insurerId);
          setAutoDetected(true);
        }
        if (data.detectedYear && data.detectedMonth) {
          setYear(String(data.detectedYear));
          setMonth(String(data.detectedMonth));
          setAutoDetectedDate(true);
        }
      },
    });
  }

  function handleUpload() {
    if (!file) return;

    if (isMultiInsurer && selectedInsurerIds.length > 1) {
      // Multiple insurers selected → batch flow
      batchPreviewMutation.mutate(
        { file, insurerIds: selectedInsurerIds, year: Number(year), month: Number(month) },
        {
          onSuccess: (data) => {
            setBatchPreviews(data.items);
            setCurrentBatchIndex(0);
            setBatchResults([]);
            setStep("preview");
          },
        },
      );
      return;
    }

    // Single insurer — use normal flow (even if multi-detected but only 1 checked)
    const effectiveInsurerId = isMultiInsurer && selectedInsurerIds.length === 1
      ? selectedInsurerIds[0]
      : insurerId;
    if (!effectiveInsurerId) return;
    previewMutation.mutate(
      { file, insurerId: effectiveInsurerId, year: Number(year), month: Number(month) },
      {
        onSuccess: (data) => {
          setPreview(data);
          setStep("preview");
        },
      },
    );
  }

  // Build orphaned assignments from the preview rows, falling back to the server-suggested
  // agent for any row the operator did not touch. An explicit empty string (operator cleared
  // the field) is respected and skipped — only `undefined` (untouched) inherits the suggestion.
  function resolveOrphanedAssignments(
    orphans: OrphanedPolicyPreview[],
  ): OrphanedAssignment[] {
    return orphans
      .map((p) => ({
        rowNumber: p.rowNumber,
        agentId: orphanedAssignments[p.rowNumber] ?? p.suggestedAgentId ?? "",
      }))
      .filter((a) => a.agentId !== "");
  }

  function handleConfirm() {
    if (!preview) return;
    const ofwcaMappings: OfwcaMapping[] = Object.entries(mappings)
      .filter(([, agentId]) => agentId !== "")
      .map(([ofwcaIdentifier, agentId]) => ({ ofwcaIdentifier, agentId }));

    const currentOverrides: PolicyOverride[] = Object.entries(policyOverrides)
      .filter(([, agentId]) => agentId)
      .flatMap(([key, agentId]) => {
        const sep = key.indexOf("::");
        if (sep < 0) return [];
        return [{ ofwcaIdentifier: key.slice(0, sep), policyNumber: key.slice(sep + 2), agentId }];
      });

    const orphanedList = resolveOrphanedAssignments(preview.orphanedPolicies);

    confirmMutation.mutate(
      {
        importId: preview.importId,
        mappings: ofwcaMappings,
        policyOverrides: currentOverrides.length > 0 ? currentOverrides : undefined,
        orphanedAssignments: orphanedList.length > 0 ? orphanedList : undefined,
      },
      {
        onSuccess: (data) => {
          setResult(data);
          setStep("result");
        },
      },
    );
  }

  function handleReset() {
    setStep("upload");
    setFile(null);
    setInsurerId("");
    setPreview(null);
    setResult(null);
    setMappings({});
    setOrphanedAssignments({});
    setPolicyOverrides({});
    setExpandedOfwca(new Set());
    setDetection(null);
    setAutoDetected(false);
    setAutoDetectedDate(false);
    setSelectedInsurerIds([]);
    setBatchPreviews([]);
    setCurrentBatchIndex(0);
    setBatchResults([]);
    previewMutation.reset();
    confirmMutation.reset();
    detectMutation.reset();
    batchPreviewMutation.reset();
    cancelImportMutation.reset();
  }

  function handleInsurerChange(value: string) {
    setInsurerId(value);
    if (autoDetected) setAutoDetected(false);
  }

  function updateMapping(ofwcaId: string, agentId: string) {
    setMappings((prev) => ({ ...prev, [ofwcaId]: agentId }));
  }

  function toggleOfwcaExpand(ofwcaId: string) {
    setExpandedOfwca(prev => {
      const next = new Set(prev);
      if (next.has(ofwcaId)) next.delete(ofwcaId);
      else next.add(ofwcaId);
      return next;
    });
  }

  const canUpload = file && year && month && (isMultiInsurer ? selectedInsurerIds.length > 0 : !!insurerId);
  const unmappedCount =
    preview?.unknownIdentifiers.filter((u) => !mappings[u.ofwcaIdentifier])
      .length ?? 0;

  return (
    <div className="space-y-4">
      {/* Stepper */}
      <Card padding="sm">
        <div className="flex items-center gap-2">
          {STEPS.map((s, i) => {
            const isActive = s === step;
            const isDone = STEPS.indexOf(step) > i;
            return (
              <div key={s} className="flex items-center gap-2">
                {i > 0 && <ChevronRight size={14} className="text-faint" />}
                <div
                  className={clsx(
                    "flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium",
                    isActive &&
                      "bg-adara-50 text-adara-700 dark:bg-adara-900/30 dark:text-adara-300",
                    isDone &&
                      "bg-green-50 text-green-700 dark:text-green-300 dark:bg-green-900/30 dark:text-green-300",
                    !isActive && !isDone && "text-faint",
                  )}
                >
                  {isDone ? (
                    <Check size={12} />
                  ) : (
                    <span className="flex h-4 w-4 items-center justify-center rounded-full bg-current/10 text-[10px]">
                      {i + 1}
                    </span>
                  )}
                  {STEP_LABELS[s]}
                </div>
              </div>
            );
          })}
        </div>
      </Card>

      {/* Step 1: Upload */}
      {step === "upload" && (
        <Card>
          <div className="space-y-4">
            {/* File upload zone — moved above selects so detection happens first */}
            <div
              onDragOver={handleDragOver}
              onDragEnter={handleDragEnter}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
              className={clsx(
                "relative rounded-lg border-2 border-dashed p-8 text-center cursor-pointer transition-colors duration-200",
                isDragging
                  ? "border-blue-500 bg-blue-50 dark:bg-blue-950/20"
                  : "border-edge hover:border-gray-400 dark:hover:border-gray-500",
              )}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.xls,.csv,.pdf"
                onChange={handleFileChange}
                className="hidden"
              />
              <div className={clsx("transition-opacity duration-200", isDragging && "opacity-30")}>
                <Upload size={32} className="mx-auto mb-3 text-faint" />
                <p className="mb-1 text-sm text-sub">
                  Przeciągnij plik tutaj lub kliknij, aby wybrać
                </p>
                <p className="text-xs text-faint">
                  .xlsx, .xls, .csv, .pdf — max 10 MB
                </p>
                {file && (
                  <p className="mt-3 text-sm text-sub">
                    <FileUp size={14} className="mr-1 inline" />
                    {file.name} ({(file.size / 1024).toFixed(0)} KB)
                  </p>
                )}
              </div>
              {isDragging && (
                <div className="absolute inset-0 flex items-center justify-center rounded-lg bg-blue-50/85 dark:bg-blue-950/85">
                  <div className="text-center">
                    <Upload size={28} className="mx-auto mb-2 text-blue-500" />
                    <p className="font-medium text-blue-600 dark:text-blue-400">Upuść plik tutaj</p>
                  </div>
                </div>
              )}
            </div>
            {dropError && (
              <p role="alert" className="mt-2 text-sm text-red-600 dark:text-red-400">{dropError}</p>
            )}

            {/* Auto-detection feedback */}
            {detectMutation.isPending && (
              <div className="flex items-center gap-2 rounded-lg bg-blue-50 p-3 text-sm text-blue-700 dark:bg-blue-900/20 dark:text-blue-300">
                <Loader2 size={16} className="animate-spin" />
                Analizuję plik...
              </div>
            )}

            {(autoDetected || autoDetectedDate) && detection && (
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1 rounded-lg bg-green-50 p-3 text-sm text-green-700 dark:text-green-300 dark:bg-green-900/20 dark:text-green-300">
                <Sparkles size={16} className="shrink-0" />
                {autoDetected && detection.detectedInsurer && (
                  <span className="flex items-center gap-1.5">
                    Ubezpieczyciel:{" "}
                    <strong>{detection.detectedInsurer.insurerName}</strong>
                    <Badge variant="success">
                      {Math.round(detection.detectedInsurer.score * 100)}%
                    </Badge>
                  </span>
                )}
                {autoDetectedDate &&
                  detection.detectedYear &&
                  detection.detectedMonth && (
                    <span className="flex items-center gap-1.5">
                      <Calendar size={14} />
                      Okres:{" "}
                      <strong>
                        {
                          MONTHS.find(
                            (m) => m.value === String(detection.detectedMonth),
                          )?.label
                        }{" "}
                        {detection.detectedYear}
                      </strong>
                    </span>
                  )}
              </div>
            )}

            {isMultiInsurer && detection && (
              <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 dark:border-blue-800 dark:bg-blue-950/20">
                <p className="mb-3 text-sm font-medium text-blue-700 dark:text-blue-300">
                  Wykryto {highConfidenceCandidates.length} ubezpieczycieli w pliku:
                </p>
                <div className="space-y-2">
                  {highConfidenceCandidates.map((c) => (
                    <label key={c.insurerId} className="flex items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        checked={selectedInsurerIds.includes(c.insurerId)}
                        onChange={(e) => {
                          setSelectedInsurerIds((prev) =>
                            e.target.checked
                              ? [...prev, c.insurerId]
                              : prev.filter((id) => id !== c.insurerId),
                          );
                        }}
                        className="rounded border-gray-300"
                      />
                      <span className="text-main">{c.insurerName}</span>
                      <Badge variant="info">
                        {Math.round(c.score * 100)}%
                      </Badge>
                    </label>
                  ))}
                </div>
              </div>
            )}

            {!isMultiInsurer && detection &&
              !detection.isExactMatch &&
              detection.candidates.length > 0 && (
                <div className="rounded-lg bg-amber-50 p-3 text-sm text-amber-700 dark:bg-amber-900/20 dark:text-amber-300">
                  <div className="mb-2 flex items-center gap-2">
                    <AlertCircle size={16} />
                    Nie udało się jednoznacznie wykryć ubezpieczyciela.
                    Sugestie:
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {detection.candidates.map((c) => (
                      <button
                        key={c.insurerId}
                        type="button"
                        onClick={() => {
                          setInsurerId(c.insurerId);
                          setAutoDetected(false);
                        }}
                        className={clsx(
                          "rounded-md border px-3 py-1 text-xs font-medium transition-colors",
                          insurerId === c.insurerId
                            ? "border-adara-500 bg-adara-50 text-adara-700 dark:bg-adara-900/30 dark:text-adara-300"
                            : "border-edge bg-panel text-sub hover:border-adara-300",
                        )}
                      >
                        {c.insurerName} ({Math.round(c.score * 100)}%)
                      </button>
                    ))}
                  </div>
                </div>
              )}

            {detection &&
              !detection.isExactMatch &&
              detection.candidates.length === 0 && (
                <div className="flex items-center gap-2 rounded-lg bg-subtle p-3 text-sm text-sub">
                  <AlertCircle size={16} />
                  Nie wykryto ubezpieczyciela — wybierz ręcznie z listy poniżej.
                </div>
              )}

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              {!isMultiInsurer && (
                <Select
                  label="Ubezpieczyciel"
                  value={insurerId}
                  onChange={handleInsurerChange}
                  options={
                    insurersLoading
                      ? [{ value: "", label: "Ładowanie..." }]
                      : (insurers ?? []).map((i) => ({
                          value: i.id,
                          label: i.name,
                        }))
                  }
                  placeholder="Wybierz ubezpieczyciela..."
                />
              )}
              <Select
                label="Rok"
                value={year}
                onChange={(v) => {
                  setYear(v);
                  setAutoDetectedDate(false);
                }}
                options={yearOptions()}
              />
              <Select
                label="Miesiąc"
                value={month}
                onChange={(v) => {
                  setMonth(v);
                  setAutoDetectedDate(false);
                }}
                options={MONTHS}
              />
            </div>

            <div className="flex justify-end">
              <Button
                onClick={handleUpload}
                disabled={!canUpload || previewMutation.isPending || batchPreviewMutation.isPending}
                isLoading={previewMutation.isPending || batchPreviewMutation.isPending}
                leftIcon={<Upload size={16} />}
              >
                Prześlij i podgląd
              </Button>
            </div>

            {previewMutation.isError && (
              <div className="flex items-center gap-2 rounded-lg bg-red-50 p-3 text-sm text-red-700 dark:text-red-300 dark:bg-red-900/20 dark:text-red-300">
                <AlertCircle size={16} />
                {(previewMutation.error as Error).message ??
                  "Wystąpił błąd podczas przetwarzania pliku."}
              </div>
            )}

            {batchPreviewMutation.isError && (
              <div className="flex items-center gap-2 rounded-lg bg-red-50 p-3 text-sm text-red-700 dark:text-red-300 dark:bg-red-900/20 dark:text-red-300">
                <AlertCircle size={16} />
                {(batchPreviewMutation.error as Error).message ??
                  "Wystąpił błąd podczas przetwarzania pliku."}
              </div>
            )}
          </div>
        </Card>
      )}

      {/* Step 2: Preview & Mapping — Batch wizard */}
      {step === "preview" && batchPreviews.length > 0 && (() => {
        const currentItem = batchPreviews[currentBatchIndex];
        if (!currentItem) return null;
        const isLast = currentBatchIndex === batchPreviews.length - 1;
        const currentPreview = currentItem.preview;

        return (
          <div className="space-y-4">
            {/* Batch progress + info header */}
            <Card padding="sm">
              <div className="mb-3 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <h3 className="text-sm font-semibold text-main">
                    {currentItem.insurerName}
                  </h3>
                  <Badge variant="info">
                    {currentBatchIndex + 1} z {batchPreviews.length}
                  </Badge>
                </div>
                <div className="flex gap-1">
                  {batchPreviews.map((_, i) => (
                    <div
                      key={i}
                      className={clsx(
                        "h-1.5 w-6 rounded-full",
                        i < currentBatchIndex ? "bg-green-400" :
                        i === currentBatchIndex ? "bg-blue-500" :
                        "bg-gray-200 dark:bg-gray-700",
                      )}
                    />
                  ))}
                </div>
              </div>
              {currentItem.error ? (
                <div className="rounded-lg bg-red-50 p-3 text-sm text-red-700 dark:bg-red-950/20 dark:text-red-300">
                  <AlertCircle size={16} className="mr-1 inline" />
                  {currentItem.error}
                </div>
              ) : currentPreview ? (
                <div className="flex flex-wrap items-center gap-4 text-sm">
                  <span className="text-dim">
                    Plik: <strong className="text-main">{currentPreview.fileName}</strong>
                  </span>
                  <span className="text-dim">
                    Okres:{" "}
                    <strong className="text-main">
                      {MONTHS.find((m) => m.value === String(currentPreview.month))?.label}{" "}
                      {currentPreview.year}
                    </strong>
                  </span>
                  <span className="text-dim">
                    Rekordy: <strong className="text-main">{currentPreview.totalRecords}</strong>
                  </span>
                </div>
              ) : null}
            </Card>

            {/* Known identifiers for batch item */}
            {currentPreview && currentPreview.knownIdentifiers.length > 0 && (
              <Card padding="none">
                <div className="border-b border-edge px-4 py-3">
                  <h3 className="flex items-center gap-2 text-sm font-medium text-main">
                    <CheckCircle size={16} className="text-green-500" />
                    Rozpoznane ({currentPreview.knownIdentifiers.length})
                  </h3>
                </div>
                <OfwcaTable
                  items={currentPreview.knownIdentifiers}
                  identifierLabel={currentPreview.agentIdentifierColumnName}
                  displayColumnNames={currentPreview.displayColumnNames}
                  mappings={mappings}
                  onChangeAgent={updateMapping}
                  expandedOfwca={expandedOfwca}
                  onToggleExpand={toggleOfwcaExpand}
                  policyOverrides={policyOverrides}
                  onPolicyOverrideChange={(key, agentId) => {
                    setPolicyOverrides(prev => ({ ...prev, [key]: agentId }));
                  }}
                  onPolicyOverrideRemove={(key) => {
                    setPolicyOverrides(prev => {
                      const next = { ...prev };
                      delete next[key];
                      return next;
                    });
                  }}
                />
              </Card>
            )}

            {/* Unknown identifiers for batch item */}
            {currentPreview && currentPreview.unknownIdentifiers.length > 0 && (
              <Card padding="none">
                <div className="border-b border-edge px-4 py-3">
                  <h3 className="flex items-center gap-2 text-sm font-medium text-main">
                    <AlertCircle size={16} className="text-amber-500" />
                    Nierozpoznane — wymagane mapowanie (
                    {currentPreview.unknownIdentifiers.length})
                  </h3>
                </div>
                <div>
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-edge bg-subtle">
                        <th className="px-4 py-3 text-left text-xs font-medium text-dim">
                          {currentPreview.agentIdentifierColumnName}
                        </th>
                        {currentPreview.displayColumnNames.map((col) => (
                          <th
                            key={col}
                            className="px-4 py-3 text-left text-xs font-medium text-dim"
                          >
                            {col}
                          </th>
                        ))}
                        <th className="px-4 py-3 text-left text-xs font-medium text-dim">
                          Polisy
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-dim">
                          Prowizja
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-dim min-w-[220px]">
                          Przypisz do agenta
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-edge-soft">
                      {currentPreview.unknownIdentifiers.map((item) => {
                        const isExpanded = expandedOfwca.has(item.ofwcaIdentifier);
                        const hasPolicies = item.policies && item.policies.length > 0;
                        const colCount = 4 + currentPreview.displayColumnNames.length;
                        return (
                          <OfwcaTableRowGroup key={item.ofwcaIdentifier}>
                            <tr className="transition-colors hover:bg-subtle">
                              <td className="px-4 py-3 text-sm font-mono text-main">
                                {item.ofwcaIdentifier}
                              </td>
                              {currentPreview.displayColumnNames.map((col) => (
                                <td key={col} className="px-4 py-3 text-sm text-sub">
                                  {item.displayValues?.[col] ?? "\u2014"}
                                </td>
                              ))}
                              <td className="px-4 py-3 text-sm text-sub">
                                <span className="flex items-center gap-1.5">
                                  {hasPolicies && (
                                    <button
                                      type="button"
                                      onClick={() => toggleOfwcaExpand(item.ofwcaIdentifier)}
                                      className="rounded p-0.5 text-faint hover:text-sub"
                                    >
                                      {isExpanded
                                        ? <ChevronDown size={14} />
                                        : <ChevronRight size={14} />}
                                    </button>
                                  )}
                                  {item.policyCount}
                                </span>
                              </td>
                              <td className="px-4 py-3 text-sm text-sub">
                                {formatCurrency(item.totalCommission)}
                              </td>
                              <td className="px-4 py-3">
                                <AgentSelect
                                  value={mappings[item.ofwcaIdentifier] ?? ""}
                                  onChange={(agentId) => {
                                    updateMapping(item.ofwcaIdentifier, agentId);
                                  }}
                                />
                              </td>
                            </tr>
                            {isExpanded && hasPolicies && (
                              <tr>
                                <td colSpan={colCount} className="p-0">
                                  <div className="border-t border-edge bg-subtle/50 px-4 py-2">
                                    <table className="w-full text-sm">
                                      <thead>
                                        <tr className="text-left text-xs text-faint">
                                          <th className="pb-1 pr-4">Nr polisy</th>
                                          <th className="pb-1 pr-4">Klient</th>
                                          <th className="pb-1 pr-4 text-right">Inkaso</th>
                                          <th className="pb-1 pr-4 text-right">Prowizja</th>
                                          <th className="pb-1">Agent</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {item.policies!.map(policy => {
                                          const overrideKey = `${item.ofwcaIdentifier}::${policy.policyNumber}`;
                                          const overrideAgentId = policyOverrides[overrideKey];
                                          const mainAgentId = mappings[item.ofwcaIdentifier];
                                          const effectiveAgentId = overrideAgentId ?? mainAgentId ?? (item.agentId ?? "");
                                          const hasOverride = !!overrideAgentId && overrideAgentId !== (mainAgentId ?? item.agentId ?? "");
                                          return (
                                            <tr key={policy.policyNumber} className={clsx(hasOverride && "bg-amber-50 dark:bg-amber-950/20")}>
                                              <td className="py-1 pr-4 font-mono text-xs">{policy.policyNumber}</td>
                                              <td className="py-1 pr-4">{policy.clientName ?? "\u2014"}</td>
                                              <td className="py-1 pr-4 text-right">{policy.premiumAmount.toLocaleString("pl-PL", { minimumFractionDigits: 2 })}{" zł"}</td>
                                              <td className="py-1 pr-4 text-right">{policy.commissionAmount.toLocaleString("pl-PL", { minimumFractionDigits: 2 })}{" zł"}</td>
                                              <td className="py-1">
                                                <div className="flex items-center">
                                                  <AgentSelect
                                                    value={effectiveAgentId}
                                                    onChange={(agentId) => {
                                                      setPolicyOverrides(prev => ({ ...prev, [overrideKey]: agentId }));
                                                    }}
                                                    placeholder="Wybierz agenta..."
                                                    className="w-48"
                                                  />
                                                  {hasOverride && (
                                                    <button
                                                      type="button"
                                                      onClick={() => {
                                                        setPolicyOverrides(prev => {
                                                          const next = { ...prev };
                                                          delete next[overrideKey];
                                                          return next;
                                                        });
                                                      }}
                                                      className="ml-1 text-xs text-faint hover:text-main"
                                                    >
                                                      {"\u00d7"}
                                                    </button>
                                                  )}
                                                </div>
                                              </td>
                                            </tr>
                                          );
                                        })}
                                      </tbody>
                                    </table>
                                  </div>
                                </td>
                              </tr>
                            )}
                          </OfwcaTableRowGroup>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </Card>
            )}

            {/* Orphaned policies for batch item */}
            {currentPreview && currentPreview.orphanedPolicies.length > 0 && (
              <Card padding="none">
                <div className="border-b border-edge px-4 py-3">
                  <h3 className="flex items-center gap-2 text-sm font-medium text-main">
                    <AlertCircle size={16} className="text-orange-500" />
                    Polisy bez identyfikatora agenta ({currentPreview.orphanedPolicies.length})
                  </h3>
                  <p className="mt-1 text-xs text-dim">
                    Te polisy nie mają przypisanego identyfikatora agenta w pliku.
                    Możesz je przypisać ręcznie lub pominąć.
                  </p>
                </div>
                <div className="divide-y divide-edge">
                  {currentPreview.orphanedPolicies.map((policy) => (
                    <div key={policy.rowNumber} className="flex items-center gap-4 px-4 py-2.5 text-sm">
                      <span className="w-12 text-xs text-dim">w.{policy.rowNumber}</span>
                      <span className="min-w-[140px] font-mono text-xs">{policy.policyNumber}</span>
                      <span className="flex-1 truncate text-dim">{policy.clientName ?? "\u2014"}</span>
                      <span className="w-24 text-right tabular-nums">
                        {policy.premiumAmount.toLocaleString("pl-PL", { minimumFractionDigits: 2 })} zł
                      </span>
                      <span className="w-24 text-right tabular-nums">
                        {policy.commissionAmount.toLocaleString("pl-PL", { minimumFractionDigits: 2 })} zł
                      </span>
                      <div className="w-48">
                        <AgentSelect
                          value={orphanedAssignments[policy.rowNumber] ?? policy.suggestedAgentId ?? ""}
                          onChange={(val) =>
                            setOrphanedAssignments((prev) => ({ ...prev, [policy.rowNumber]: val }))
                          }
                        />
                        {policy.suggestedAgentName &&
                          orphanedAssignments[policy.rowNumber] === undefined && (
                            <p className="mt-1 truncate text-[10px] text-dim">
                              sugestia z pliku — zweryfikuj
                            </p>
                          )}
                      </div>
                    </div>
                  ))}
                </div>
              </Card>
            )}

            {/* Bottom buttons */}
            <div className="flex justify-between">
              <Button
                variant="ghost"
                onClick={() => {
                  if (currentPreview) {
                    cancelImportMutation.mutate(currentPreview.importId, {
                      onSuccess: () => {
                        setBatchResults(prev => [...prev, "skipped"]);
                        setMappings({});
                        setOrphanedAssignments({});
                        setPolicyOverrides({});
                        setExpandedOfwca(new Set());
                        if (isLast) setStep("result");
                        else setCurrentBatchIndex(prev => prev + 1);
                      },
                    });
                  } else {
                    setBatchResults(prev => [...prev, "error"]);
                    setMappings({});
                    setOrphanedAssignments({});
                    setPolicyOverrides({});
                    setExpandedOfwca(new Set());
                    if (isLast) setStep("result");
                    else setCurrentBatchIndex(prev => prev + 1);
                  }
                }}
                disabled={cancelImportMutation.isPending || confirmMutation.isPending}
              >
                Pomiń
              </Button>
              {currentPreview && !currentItem.error && (
                <Button
                  onClick={() => {
                    const importId = currentPreview.importId;
                    const currentMappings: OfwcaMapping[] = Object.entries(mappings)
                      .filter(([, agentId]) => agentId !== "")
                      .map(([ofwcaIdentifier, agentId]) => ({ ofwcaIdentifier, agentId }));

                    const currentOverrides: PolicyOverride[] = Object.entries(policyOverrides)
                      .filter(([, agentId]) => agentId)
                      .flatMap(([key, agentId]) => {
                        const sep = key.indexOf("::");
                        if (sep < 0) return [];
                        return [{ ofwcaIdentifier: key.slice(0, sep), policyNumber: key.slice(sep + 2), agentId }];
                      });

                    const orphanedList = resolveOrphanedAssignments(currentPreview.orphanedPolicies);

                    confirmMutation.mutate(
                      {
                        importId,
                        mappings: currentMappings,
                        policyOverrides: currentOverrides.length > 0 ? currentOverrides : undefined,
                        orphanedAssignments: orphanedList.length > 0 ? orphanedList : undefined,
                      },
                      {
                        onSuccess: (result) => {
                          setBatchResults(prev => [...prev, result]);
                          setMappings({});
                          setOrphanedAssignments({});
                          setPolicyOverrides({});
                          setExpandedOfwca(new Set());
                          if (isLast) setStep("result");
                          else setCurrentBatchIndex(prev => prev + 1);
                        },
                      },
                    );
                  }}
                  disabled={confirmMutation.isPending || cancelImportMutation.isPending}
                  isLoading={confirmMutation.isPending}
                  leftIcon={<Check size={16} />}
                >
                  {isLast ? "Zatwierdź import" : "Zatwierdź i przejdź dalej"}
                </Button>
              )}
            </div>

            {confirmMutation.isError && (
              <div className="flex items-center gap-2 rounded-lg bg-red-50 p-3 text-sm text-red-700 dark:text-red-300 dark:bg-red-900/20 dark:text-red-300">
                <AlertCircle size={16} />
                {(confirmMutation.error as Error).message ??
                  "Wystąpił błąd podczas importu."}
              </div>
            )}
          </div>
        );
      })()}

      {/* Step 2: Preview & Mapping — Single insurer */}
      {step === "preview" && batchPreviews.length === 0 && preview && (
        <div className="space-y-4">
          <Card padding="sm">
            <div className="flex flex-wrap items-center gap-4 text-sm">
              <span className="text-dim">
                Plik: <strong className="text-main">{preview.fileName}</strong>
              </span>
              <span className="text-dim">
                Ubezpieczyciel:{" "}
                <strong className="text-main">{preview.insurerName}</strong>
              </span>
              <span className="text-dim">
                Okres:{" "}
                <strong className="text-main">
                  {MONTHS.find((m) => m.value === String(preview.month))?.label}{" "}
                  {preview.year}
                </strong>
              </span>
              <span className="text-dim">
                Rekordy:{" "}
                <strong className="text-main">{preview.totalRecords}</strong>
              </span>
            </div>
          </Card>

          {/* Known identifiers */}
          {preview.knownIdentifiers.length > 0 && (
            <Card padding="none">
              <div className="border-b border-edge px-4 py-3">
                <h3 className="flex items-center gap-2 text-sm font-medium text-main">
                  <CheckCircle size={16} className="text-green-500" />
                  Rozpoznane ({preview.knownIdentifiers.length})
                </h3>
              </div>
              <OfwcaTable
                items={preview.knownIdentifiers}
                identifierLabel={preview.agentIdentifierColumnName}
                displayColumnNames={preview.displayColumnNames}
                mappings={mappings}
                onChangeAgent={updateMapping}
                expandedOfwca={expandedOfwca}
                onToggleExpand={toggleOfwcaExpand}
                policyOverrides={policyOverrides}
                onPolicyOverrideChange={(key, agentId) => {
                  setPolicyOverrides(prev => ({ ...prev, [key]: agentId }));
                }}
                onPolicyOverrideRemove={(key) => {
                  setPolicyOverrides(prev => {
                    const next = { ...prev };
                    delete next[key];
                    return next;
                  });
                }}
              />
            </Card>
          )}

          {/* Unknown identifiers */}
          {preview.unknownIdentifiers.length > 0 && (
            <Card padding="none">
              <div className="border-b border-edge px-4 py-3">
                <h3 className="flex items-center gap-2 text-sm font-medium text-main">
                  <AlertCircle size={16} className="text-amber-500" />
                  Nierozpoznane — wymagane mapowanie (
                  {preview.unknownIdentifiers.length})
                </h3>
                {unmappedCount > 0 && (
                  <p className="mt-1 text-xs text-amber-600 dark:text-amber-400">
                    Pozostało {unmappedCount} niezmapowanych identyfikatorów.
                    Możesz je pominąć lub przypisać do agentów.
                  </p>
                )}
              </div>
              <div>
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-edge bg-subtle">
                      <th className="px-4 py-3 text-left text-xs font-medium text-dim">
                        {preview.agentIdentifierColumnName}
                      </th>
                      {preview.displayColumnNames.map((col) => (
                        <th
                          key={col}
                          className="px-4 py-3 text-left text-xs font-medium text-dim"
                        >
                          {col}
                        </th>
                      ))}
                      <th className="px-4 py-3 text-left text-xs font-medium text-dim">
                        Polisy
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-dim">
                        Prowizja
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-dim min-w-[220px]">
                        Przypisz do agenta
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-edge-soft">
                    {preview.unknownIdentifiers.map((item) => {
                      const isExpanded = expandedOfwca.has(item.ofwcaIdentifier);
                      const hasPolicies = item.policies && item.policies.length > 0;
                      const colCount = 4 + preview.displayColumnNames.length;
                      return (
                        <OfwcaTableRowGroup key={item.ofwcaIdentifier}>
                          <tr className="transition-colors hover:bg-subtle">
                            <td className="px-4 py-3 text-sm font-mono text-main">
                              {item.ofwcaIdentifier}
                            </td>
                            {preview.displayColumnNames.map((col) => (
                              <td key={col} className="px-4 py-3 text-sm text-sub">
                                {item.displayValues?.[col] ?? "\u2014"}
                              </td>
                            ))}
                            <td className="px-4 py-3 text-sm text-sub">
                              <span className="flex items-center gap-1.5">
                                {hasPolicies && (
                                  <button
                                    type="button"
                                    onClick={() => toggleOfwcaExpand(item.ofwcaIdentifier)}
                                    className="rounded p-0.5 text-faint hover:text-sub"
                                  >
                                    {isExpanded
                                      ? <ChevronDown size={14} />
                                      : <ChevronRight size={14} />}
                                  </button>
                                )}
                                {item.policyCount}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-sm text-sub">
                              {formatCurrency(item.totalCommission)}
                            </td>
                            <td className="px-4 py-3">
                              <AgentSelect
                                value={mappings[item.ofwcaIdentifier] ?? ""}
                                onChange={(agentId) => {
                                  updateMapping(item.ofwcaIdentifier, agentId);
                                }}
                              />
                            </td>
                          </tr>
                          {isExpanded && hasPolicies && (
                            <tr>
                              <td colSpan={colCount} className="p-0">
                                <div className="border-t border-edge bg-subtle/50 px-4 py-2">
                                  <table className="w-full text-sm">
                                    <thead>
                                      <tr className="text-left text-xs text-faint">
                                        <th className="pb-1 pr-4">Nr polisy</th>
                                        <th className="pb-1 pr-4">Klient</th>
                                        <th className="pb-1 pr-4 text-right">Inkaso</th>
                                        <th className="pb-1 pr-4 text-right">Prowizja</th>
                                        <th className="pb-1">Agent</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {item.policies!.map(policy => {
                                        const overrideKey = `${item.ofwcaIdentifier}::${policy.policyNumber}`;
                                        const overrideAgentId = policyOverrides[overrideKey];
                                        const mainAgentId = mappings[item.ofwcaIdentifier];
                                        const effectiveAgentId = overrideAgentId ?? mainAgentId ?? (item.agentId ?? "");
                                        const hasOverride = !!overrideAgentId && overrideAgentId !== (mainAgentId ?? item.agentId ?? "");
                                        return (
                                          <tr key={policy.policyNumber} className={clsx(hasOverride && "bg-amber-50 dark:bg-amber-950/20")}>
                                            <td className="py-1 pr-4 font-mono text-xs">{policy.policyNumber}</td>
                                            <td className="py-1 pr-4">{policy.clientName ?? "\u2014"}</td>
                                            <td className="py-1 pr-4 text-right">{policy.premiumAmount.toLocaleString("pl-PL", { minimumFractionDigits: 2 })}{" zł"}</td>
                                            <td className="py-1 pr-4 text-right">{policy.commissionAmount.toLocaleString("pl-PL", { minimumFractionDigits: 2 })}{" zł"}</td>
                                            <td className="py-1">
                                              <div className="flex items-center">
                                                <AgentSelect
                                                  value={effectiveAgentId}
                                                  onChange={(agentId) => {
                                                    setPolicyOverrides(prev => ({ ...prev, [overrideKey]: agentId }));
                                                  }}
                                                  placeholder="Wybierz agenta..."
                                                  className="w-48"
                                                />
                                                {hasOverride && (
                                                  <button
                                                    type="button"
                                                    onClick={() => {
                                                      setPolicyOverrides(prev => {
                                                        const next = { ...prev };
                                                        delete next[overrideKey];
                                                        return next;
                                                      });
                                                    }}
                                                    className="ml-1 text-xs text-faint hover:text-main"
                                                  >
                                                    {"\u00d7"}
                                                  </button>
                                                )}
                                              </div>
                                            </td>
                                          </tr>
                                        );
                                      })}
                                    </tbody>
                                  </table>
                                </div>
                              </td>
                            </tr>
                          )}
                        </OfwcaTableRowGroup>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </Card>
          )}

          {/* Orphaned policies — no agent identifier */}
          {preview.orphanedPolicies.length > 0 && (
            <Card padding="none">
              <div className="border-b border-edge px-4 py-3">
                <h3 className="flex items-center gap-2 text-sm font-medium text-main">
                  <AlertCircle size={16} className="text-orange-500" />
                  Polisy bez identyfikatora agenta ({preview.orphanedPolicies.length})
                </h3>
                <p className="mt-1 text-xs text-dim">
                  Te polisy nie mają przypisanego identyfikatora agenta w pliku.
                  Możesz je przypisać ręcznie lub pominąć.
                </p>
              </div>
              <div className="divide-y divide-edge">
                {preview.orphanedPolicies.map((policy) => (
                  <div key={policy.rowNumber} className="flex items-center gap-4 px-4 py-2.5 text-sm">
                    <span className="w-12 text-xs text-dim">w.{policy.rowNumber}</span>
                    <span className="min-w-[140px] font-mono text-xs">{policy.policyNumber}</span>
                    <span className="flex-1 truncate text-dim">{policy.clientName ?? "\u2014"}</span>
                    <span className="w-24 text-right tabular-nums">
                      {policy.premiumAmount.toLocaleString("pl-PL", { minimumFractionDigits: 2 })} zł
                    </span>
                    <span className="w-24 text-right tabular-nums">
                      {policy.commissionAmount.toLocaleString("pl-PL", { minimumFractionDigits: 2 })} zł
                    </span>
                    <div className="w-48">
                      <AgentSelect
                        value={orphanedAssignments[policy.rowNumber] ?? policy.suggestedAgentId ?? ""}
                        onChange={(val) =>
                          setOrphanedAssignments((prev) => ({ ...prev, [policy.rowNumber]: val }))
                        }
                      />
                      {policy.suggestedAgentName &&
                        orphanedAssignments[policy.rowNumber] === undefined && (
                          <p className="mt-1 truncate text-[10px] text-dim">
                            sugestia z pliku — zweryfikuj
                          </p>
                        )}
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          )}

          <div className="flex justify-between">
            <Button variant="outline" onClick={handleReset}>
              Anuluj
            </Button>
            <Button
              onClick={handleConfirm}
              isLoading={confirmMutation.isPending}
              leftIcon={<Check size={16} />}
            >
              Potwierdź import
            </Button>
          </div>

          {confirmMutation.isError && (
            <div className="flex items-center gap-2 rounded-lg bg-red-50 p-3 text-sm text-red-700 dark:text-red-300 dark:bg-red-900/20 dark:text-red-300">
              <AlertCircle size={16} />
              {(confirmMutation.error as Error).message ??
                "Wystąpił błąd podczas importu."}
            </div>
          )}
        </div>
      )}

      {/* Step 3: Result — Batch */}
      {step === "result" && batchResults.length > 0 && (
        <Card>
          <div className="text-center">
            <CheckCircle size={48} className="mx-auto mb-3 text-green-500" />
            <h3 className="mb-4 text-lg font-semibold text-main">
              Import zakończony — {batchPreviews.length} ubezpieczycieli
            </h3>
          </div>
          <div className="space-y-3">
            {batchPreviews.map((item, i) => {
              const res = batchResults[i];
              return (
                <div key={item.insurerId} className="flex items-center justify-between rounded-lg bg-subtle p-3">
                  <span className="font-medium text-main">{item.insurerName}</span>
                  {res === "skipped" ? (
                    <span className="text-sm text-faint">pominięto</span>
                  ) : res === "error" ? (
                    <span className="text-sm text-red-500">błąd parsowania</span>
                  ) : res ? (
                    <span className="text-sm text-sub">
                      {res.successfulRecords} polis
                      {res.failedRecords > 0 && (
                        <span className="text-red-500">, {res.failedRecords} błędów</span>
                      )}
                    </span>
                  ) : null}
                </div>
              );
            })}
          </div>
          <div className="mt-6 text-center">
            <Button onClick={handleReset}>Importuj kolejny plik</Button>
          </div>
        </Card>
      )}

      {/* Step 3: Result — Single */}
      {step === "result" && batchResults.length === 0 && result && (
        <Card>
          <div className="space-y-4 text-center">
            {result.failedRecords === 0 ? (
              <CheckCircle size={48} className="mx-auto text-green-500" />
            ) : (
              <AlertCircle size={48} className="mx-auto text-amber-500" />
            )}

            <h3 className="text-lg font-semibold text-main">
              {result.failedRecords === 0
                ? "Import zakończony pomyślnie"
                : "Import zakończony z błędami"}
            </h3>

            {result.errors.length > 0 && (
              <div className="mx-auto max-w-lg rounded-lg bg-red-50 p-4 text-left dark:bg-red-900/20">
                <h4 className="mb-2 flex items-center gap-1 text-sm font-medium text-red-700 dark:text-red-300">
                  <XCircle size={14} />
                  Szczegóły błędów
                </h4>
                <ul className="space-y-1 text-xs text-red-600 dark:text-red-400">
                  {result.errors.map((error, i) => (
                    <li key={i}>{error}</li>
                  ))}
                </ul>
              </div>
            )}

            <Button onClick={handleReset} variant="outline">
              Importuj kolejny plik
            </Button>
          </div>
        </Card>
      )}
    </div>
  );
}

function ImportHistorySection() {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [statusFilter, setStatusFilter] = useState("");
  const { data, isLoading } = useImportHistory(page, pageSize, statusFilter || undefined);
  const deleteMutation = useDeleteImport();
  const [deleteItem, setDeleteItem] = useState<ImportHistoryItem | null>(null);

  function executeDelete() {
    if (!deleteItem) return;
    deleteMutation.mutate(deleteItem.id, {
      onSuccess: () => setDeleteItem(null),
    });
  }

  if (isLoading) {
    return (
      <Card>
        <div className="flex items-center justify-center py-12">
          <Loader2 size={24} className="animate-spin text-faint" />
        </div>
      </Card>
    );
  }

  const items = data?.items ?? [];

  if (items.length === 0) {
    return (
      <Card>
        <div className="py-12 text-center text-sm text-dim">
          <History size={32} className="mx-auto mb-3 text-faint" />
          Brak historii importów.
        </div>
      </Card>
    );
  }

  return (
    <>
      <div className="flex items-center gap-3">
        <Select
          size="sm"
          value={statusFilter}
          onChange={(v) => {
            setStatusFilter(v);
            setPage(1);
          }}
          options={[
            { value: "", label: "Wszystkie statusy" },
            { value: "Completed", label: "Zakończony" },
            { value: "CompletedWithErrors", label: "Z błędami" },
            { value: "Failed", label: "Nieudany" },
            { value: "Processing", label: "W trakcie" },
            { value: "Pending", label: "Oczekujący" },
          ]}
        />
      </div>
      <Card padding="none">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-edge bg-subtle">
                <th className="px-4 py-3 text-left text-xs font-medium text-dim">
                  Plik
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-dim">
                  Ubezpieczyciel
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-dim">
                  Status
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-dim">
                  Rekordy
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-dim">
                  Sukces
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-dim">
                  Błędy
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-dim">
                  Utworzono
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-dim">
                  Akcje
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-edge-soft">
              {items.map((item) => {
                const statusInfo = STATUS_MAP[item.status] ?? {
                  label: item.status,
                  variant: "default" as const,
                };
                return (
                  <tr
                    key={item.id}
                    className="transition-colors hover:bg-subtle"
                  >
                    <td className="px-4 py-3 text-sm text-main">
                      {item.fileName}
                    </td>
                    <td className="px-4 py-3 text-sm text-sub">
                      {item.insurerName}
                    </td>
                    <td className="px-4 py-3">
                      <Badge variant={statusInfo.variant} dot>
                        {statusInfo.label}
                      </Badge>
                    </td>
                    <td className="px-4 py-3 text-right text-sm text-sub">
                      {item.totalRecords}
                    </td>
                    <td className="px-4 py-3 text-right text-sm text-green-700 dark:text-green-300">
                      {item.successfulRecords}
                    </td>
                    <td className="px-4 py-3 text-right text-sm text-red-700 dark:text-red-300">
                      {item.failedRecords}
                    </td>
                    <td className="px-4 py-3 text-sm text-dim">
                      <span className="flex items-center gap-1">
                        <Clock size={12} />
                        {new Date(item.createdAt).toLocaleString("pl-PL", {
                          day: "2-digit",
                          month: "2-digit",
                          year: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        type="button"
                        onClick={() => setDeleteItem(item)}
                        className="rounded-md p-1.5 text-red-600 dark:text-red-400 transition-colors hover:bg-red-50 dark:hover:bg-red-900/20"
                        title="Usuń import"
                      >
                        <Trash2 size={16} />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {(data?.totalCount ?? 0) > 0 && (
          <UsersPagination
            page={page}
            pageSize={pageSize}
            totalCount={data?.totalCount ?? 0}
            totalPages={data?.totalPages ?? 1}
            onPageChange={setPage}
            onPageSizeChange={(size) => {
              setPageSize(size);
              setPage(1);
            }}
          />
        )}
      </Card>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={deleteItem !== null}
        onClose={() => setDeleteItem(null)}
        title="Usuń import"
      >
        <div className="space-y-4">
          <p className="text-sm text-sub">
            Czy na pewno chcesz usunąć import{" "}
            <strong>{deleteItem?.fileName}</strong>? Zostaną usunięte wszystkie
            polisy powiązane z tym importem. Tej operacji nie można cofnąć.
          </p>

          {deleteMutation.isError && (
            <div className="flex items-center gap-2 rounded-lg bg-red-50 p-3 text-sm text-red-700 dark:text-red-300 dark:bg-red-900/20 dark:text-red-300">
              <AlertCircle size={16} />
              {(deleteMutation.error as Error)?.message ?? "Wystąpił błąd."}
            </div>
          )}

          <div className="flex justify-end gap-3">
            <Button
              variant="outline"
              onClick={() => {
                setDeleteItem(null);
                deleteMutation.reset();
              }}
            >
              Anuluj
            </Button>
            <Button
              variant="danger"
              onClick={executeDelete}
              isLoading={deleteMutation.isPending}
              leftIcon={<Trash2 size={16} />}
            >
              Usuń
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
}

function OfwcaTable({
  items,
  identifierLabel,
  displayColumnNames,
  mappings,
  onChangeAgent,
  expandedOfwca,
  onToggleExpand,
  policyOverrides,
  onPolicyOverrideChange,
  onPolicyOverrideRemove,
}: {
  items: OfwcaPreviewItem[];
  identifierLabel: string;
  displayColumnNames: string[];
  mappings?: Record<string, string>;
  onChangeAgent?: (ofwcaId: string, agentId: string) => void;
  expandedOfwca?: Set<string>;
  onToggleExpand?: (ofwcaId: string) => void;
  policyOverrides?: Record<string, string>;
  onPolicyOverrideChange?: (key: string, agentId: string) => void;
  onPolicyOverrideRemove?: (key: string) => void;
}) {
  const [editingId, setEditingId] = useState<string | null>(null);

  return (
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead>
          <tr className="border-b border-edge bg-subtle">
            <th className="px-4 py-3 text-left text-xs font-medium text-dim">
              Agent
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium text-dim">
              {identifierLabel}
            </th>
            {displayColumnNames.map((col) => (
              <th
                key={col}
                className="px-4 py-3 text-left text-xs font-medium text-dim"
              >
                {col}
              </th>
            ))}
            <th className="px-4 py-3 text-left text-xs font-medium text-dim">
              Polisy
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium text-dim">
              Prowizja
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-edge-soft">
          {items.map((item) => {
            const isEditing = editingId === item.ofwcaIdentifier;
            const hasAgentOverride = mappings?.[item.ofwcaIdentifier];
            const isExpanded = expandedOfwca?.has(item.ofwcaIdentifier) ?? false;
            const hasPolicies = item.policies && item.policies.length > 0;
            return (
              <OfwcaTableRowGroup key={item.ofwcaIdentifier}>
                <tr className="transition-colors hover:bg-subtle">
                  <td className="px-4 py-3 text-sm text-main">
                    {isEditing ? (
                      <AgentSelect
                        value={hasAgentOverride ?? item.agentId ?? ""}
                        onChange={(agentId) => {
                          onChangeAgent?.(item.ofwcaIdentifier, agentId);
                          setEditingId(null);
                        }}
                        className="w-56"
                      />
                    ) : (
                      <span className="flex items-center gap-1.5">
                        <span
                          className={
                            hasAgentOverride ? "text-adara-700 font-medium" : ""
                          }
                        >
                          {item.agentName ?? "\u2014"}
                          {hasAgentOverride && " (zmieniony)"}
                        </span>
                        {onChangeAgent && (
                          <button
                            type="button"
                            onClick={() => setEditingId(item.ofwcaIdentifier)}
                            className="rounded p-0.5 text-faint hover:bg-muted hover:text-sub"
                            title="Zmie\u0144 przypisanego agenta"
                          >
                            <Pencil size={13} aria-hidden="true" />
                          </button>
                        )}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-sm font-mono text-dim">
                    {item.ofwcaIdentifier}
                  </td>
                  {displayColumnNames.map((col) => (
                    <td key={col} className="px-4 py-3 text-sm text-sub">
                      {item.displayValues?.[col] ?? "\u2014"}
                    </td>
                  ))}
                  <td className="px-4 py-3 text-sm text-sub">
                    <span className="flex items-center gap-1.5">
                      {hasPolicies && onToggleExpand && (
                        <button
                          type="button"
                          onClick={() => onToggleExpand(item.ofwcaIdentifier)}
                          className="rounded p-0.5 text-faint hover:text-sub"
                        >
                          {isExpanded
                            ? <ChevronDown size={14} />
                            : <ChevronRight size={14} />}
                        </button>
                      )}
                      {item.policyCount}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-sub">
                    {formatCurrency(item.totalCommission)}
                  </td>
                </tr>
                {isExpanded && hasPolicies && (
                  <tr>
                    <td colSpan={4 + displayColumnNames.length} className="p-0">
                      <div className="border-t border-edge bg-subtle/50 px-4 py-2">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="text-left text-xs text-faint">
                              <th className="pb-1 pr-4">Nr polisy</th>
                              <th className="pb-1 pr-4">Klient</th>
                              <th className="pb-1 pr-4 text-right">Inkaso</th>
                              <th className="pb-1 pr-4 text-right">Prowizja</th>
                              <th className="pb-1">Agent</th>
                            </tr>
                          </thead>
                          <tbody>
                            {item.policies!.map(policy => {
                              const overrideKey = `${item.ofwcaIdentifier}::${policy.policyNumber}`;
                              const overrideAgentId = policyOverrides?.[overrideKey];
                              // Effective agent: override > main select mapping > pre-existing DB mapping
                              const mainMappingAgentId = mappings?.[item.ofwcaIdentifier];
                              const effectiveAgentId = overrideAgentId ?? mainMappingAgentId ?? (item.agentId ?? "");
                              const hasOverride = !!overrideAgentId && overrideAgentId !== (mainMappingAgentId ?? item.agentId ?? "");

                              return (
                                <tr key={policy.policyNumber} className={clsx(hasOverride && "bg-amber-50 dark:bg-amber-950/20")}>
                                  <td className="py-1 pr-4 font-mono text-xs">{policy.policyNumber}</td>
                                  <td className="py-1 pr-4">{policy.clientName ?? "\u2014"}</td>
                                  <td className="py-1 pr-4 text-right">{policy.premiumAmount.toLocaleString("pl-PL", { minimumFractionDigits: 2 })}{" zł"}</td>
                                  <td className="py-1 pr-4 text-right">{policy.commissionAmount.toLocaleString("pl-PL", { minimumFractionDigits: 2 })}{" zł"}</td>
                                  <td className="py-1">
                                    <div className="flex items-center">
                                      <AgentSelect
                                        value={effectiveAgentId}
                                        onChange={(agentId) => {
                                          onPolicyOverrideChange?.(overrideKey, agentId);
                                        }}
                                        placeholder="Wybierz agenta..."
                                        className="w-48"
                                      />
                                      {hasOverride && (
                                        <button
                                          type="button"
                                          onClick={() => {
                                            onPolicyOverrideRemove?.(overrideKey);
                                          }}
                                          className="ml-1 text-xs text-faint hover:text-main"
                                        >
                                          {"\u00d7"}
                                        </button>
                                      )}
                                    </div>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </td>
                  </tr>
                )}
              </OfwcaTableRowGroup>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/** Wrapper fragment for grouping OFWCA row + accordion expansion row */
function OfwcaTableRowGroup({ children }: { children: React.ReactNode }) {
  return <>{children}</>;
}
